import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile } from '../../services/profileService';
import { getCurrentUser } from '../../services/authService';
import ProfileForm from '../../components/Profile/ProfileForm';
import Spinner from '../../components/common/Spinner';
import './Profile.css';

/**
 * Profile component for displaying and editing user profile information
 * Supports both artist and venue profiles
 */
const Profile = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        // Get the current user
        const { user, error: userError } = await getCurrentUser();
        
        if (userError) throw userError;
        
        if (user) {
          setUser(user);
          
          // Fetch the user's profile
          const { profile, error: profileError } = await getProfile(user.id);
            
          if (profileError) {
            throw profileError;
          }
          
          // Set the profile (might be null if it doesn't exist yet)
          setProfile(profile);
          
          // If no profile exists, automatically go into edit mode
          if (!profile) {
            console.log('No profile found, entering edit mode');
            setIsEditing(true);
          }
        } else {
          // Redirect to login if no user is found
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching profile:', error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [navigate]);

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const handleProfileUpdate = (updatedProfile) => {
    setProfile(updatedProfile);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="dashboard-section">
          <Spinner message="Loading profile..." fullPage={true} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container">
        <div className="dashboard-alert error">
          <div className="alert-content">
            <h4>Error!</h4>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // If in edit mode, show the profile form
  if (isEditing) {
    return (
      <div className="profile-container">
        <div className="dashboard-header">
          <h1>Edit Your Profile</h1>
          <p>Update your information to increase your visibility</p>
        </div>
        <ProfileForm 
          initialData={profile} 
          userId={user.id} 
          onSuccess={handleProfileUpdate} 
          onCancel={toggleEditMode} 
        />
      </div>
    );
  }

  // Default view mode
  return (
    <div className="profile-container">
      <div className="dashboard-header">
        <h1>Your Profile</h1>
        <p>Your professional information and portfolio</p>
        <button 
          className="edit-profile-btn" 
          onClick={toggleEditMode}
        >
          <i className="bi bi-pencil-square"></i> Edit Profile
        </button>
      </div>
      
      <div className="dashboard-card profile-card">
        <div className="card-header">
          <h2><i className="bi bi-person-circle"></i> Profile Information</h2>
        </div>
            
        {profile ? (
          <div className="card-body">
            <div className="profile-content">
              <div className="profile-image-container">
                {profile.profile_image_url ? (
                  <>
                    <img 
                      src={profile.profile_image_url} 
                      alt={profile.full_name || 'Profile'} 
                      className="profile-picture"
                      onError={(e) => {
                        console.error('Error loading profile image:', profile.profile_image_url);
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/150';
                      }}
                    />
                    <button 
                      className="view-image-btn"
                      onClick={() => window.open(profile.profile_image_url, '_blank')}
                    >
                      <i className="bi bi-box-arrow-up-right"></i>
                    </button>
                  </>
                ) : (
                  <div className="profile-picture-placeholder">
                    <span>
                      {profile.full_name ? profile.full_name.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="profile-info">
                <h3 className="profile-name">{profile.full_name || user.email}</h3>
                <p className="profile-role">
                  {profile.user_type === 'artist' ? 
                    (profile.professions && profile.professions.length > 0 ? profile.professions[0] : 'Artist') : 
                    'Venue'}
                </p>
              </div>
              
              <div className="profile-section">
                <h4 className="profile-section-title"><i className="bi bi-info-circle"></i> About</h4>
                <p className="profile-section-content">{profile.bio || 'No bio available'}</p>
              </div>
              


              <div className="profile-section">
                <h4 className="profile-section-title"><i className="bi bi-envelope"></i> Contact Information</h4>
                <div className="contact-info-grid">
                  <div className="contact-info-item">
                    <i className="bi bi-envelope-fill"></i>
                    <span>{profile.contact_email || user.email}</span>
                  </div>
                  {profile.phone && (
                    <div className="contact-info-item">
                      <i className="bi bi-telephone-fill"></i>
                      <span>{profile.phone}</span>
                    </div>
                  )}
                  {profile.location && (
                    <div className="contact-info-item">
                      <i className="bi bi-geo-alt-fill"></i>
                      <span>{profile.location}</span>
                    </div>
                  )}
                  {profile.website && (
                    <div className="contact-info-item">
                      <i className="bi bi-globe"></i>
                      <a href={profile.website} target="_blank" rel="noopener noreferrer">
                        {profile.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Social Links Section */}
              {(profile.instagram_url || profile.facebook_url || profile.tiktok_url || 
                profile.youtube_url || profile.soundcloud_url || profile.bandcamp_url || 
                profile.spotify_url) && (
                <div className="profile-section">
                  <h4 className="profile-section-title"><i className="bi bi-share"></i> Social Links</h4>
                  <div className="social-links-grid">
                    {profile.instagram_url && (
                      <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-instagram"></i> Instagram
                      </a>
                    )}
                    {profile.facebook_url && (
                      <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-facebook"></i> Facebook
                      </a>
                    )}
                    {profile.tiktok_url && (
                      <a href={profile.tiktok_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-tiktok"></i> TikTok
                      </a>
                    )}
                    {profile.youtube_url && (
                      <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-youtube"></i> YouTube
                      </a>
                    )}
                    {profile.soundcloud_url && (
                      <a href={profile.soundcloud_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-soundwave"></i> SoundCloud
                      </a>
                    )}
                    {profile.bandcamp_url && (
                      <a href={profile.bandcamp_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-music-note-beamed"></i> Bandcamp
                      </a>
                    )}
                    {profile.spotify_url && (
                      <a href={profile.spotify_url} target="_blank" rel="noopener noreferrer" className="social-link-item">
                        <i className="bi bi-spotify"></i> Spotify
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Artist-specific information */}
              {profile.user_type === 'artist' && (
                <div className="profile-section">
                  <h4 className="profile-section-title"><i className="bi bi-music-note-beamed"></i> Artist Information</h4>
                  
                  {profile.band_name && (
                    <div className="contact-info-item">
                      <i className="bi bi-people-fill"></i>
                      <span><strong>Band/Group:</strong> {profile.band_name}</span>
                    </div>
                  )}
                  
                  {profile.professions && profile.professions.length > 0 && (
                    <div className="contact-info-item">
                      <i className="bi bi-briefcase-fill"></i>
                      <span><strong>Professions:</strong></span>
                    </div>
                  )}
                  
                  {profile.professions && profile.professions.length > 0 && (
                    <div className="profile-tags">
                      {profile.professions.map((profession, index) => (
                        <span key={`profession-${index}`} className="profile-tag">
                          <i className="bi bi-person-workspace"></i>
                          {profession}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {profile.genres && profile.genres.length > 0 && (
                    <div className="contact-info-item">
                      <i className="bi bi-vinyl-fill"></i>
                      <span><strong>Genres:</strong></span>
                    </div>
                  )}
                  
                  {profile.genres && profile.genres.length > 0 && (
                    <div className="profile-tags">
                      {profile.genres.map((genre, index) => (
                        <span key={`genre-${index}`} className="profile-tag">
                          <i className="bi bi-music-note"></i>
                          {genre}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {profile.instruments && profile.instruments.length > 0 && (
                    <div className="contact-info-item">
                      <i className="bi bi-speaker-fill"></i>
                      <span><strong>Instruments:</strong></span>
                    </div>
                  )}
                  
                  {profile.instruments && profile.instruments.length > 0 && (
                    <div className="profile-tags">
                      {profile.instruments.map((instrument, index) => (
                        <span key={`instrument-${index}`} className="profile-tag">
                          <i className="bi bi-soundwave"></i>
                          {instrument}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {profile.rate_per_gig && (
                    <div className="contact-info-item">
                      <i className="bi bi-cash-coin"></i>
                      <span><strong>Rate per Gig:</strong> <span className="artist-rate">${profile.rate_per_gig}</span></span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Venue-specific information */}
              {profile.user_type === 'venue' && (
                <div className="profile-section">
                  <h4 className="profile-section-title"><i className="bi bi-building"></i> Venue Information</h4>
                  
                  {profile.venue_name && (
                    <div className="contact-info-item">
                      <i className="bi bi-house-door-fill"></i>
                      <span><strong>Venue Name:</strong> {profile.venue_name}</span>
                    </div>
                  )}
                  
                  {profile.venue_type && (
                    <div className="contact-info-item">
                      <i className="bi bi-building-fill"></i>
                      <span><strong>Venue Type:</strong> {profile.venue_type}</span>
                    </div>
                  )}
                  
                  {profile.organizer_title && (
                    <div className="contact-info-item">
                      <i className="bi bi-person-badge-fill"></i>
                      <span><strong>Title/Position:</strong> {profile.organizer_title}</span>
                    </div>
                  )}
                  
                  {profile.capacity && (
                    <div className="contact-info-item">
                      <i className="bi bi-people-fill"></i>
                      <span><strong>Capacity:</strong> {profile.capacity}</span>
                    </div>
                  )}
                  
                  {profile.amenities && profile.amenities.length > 0 && (
                    <div className="contact-info-item">
                      <i className="bi bi-list-check"></i>
                      <span><strong>Amenities:</strong></span>
                    </div>
                  )}
                  
                  {profile.amenities && profile.amenities.length > 0 && (
                    <div className="profile-tags">
                      {profile.amenities.map((amenity, index) => (
                        <span key={`amenity-${index}`} className="profile-tag">
                          <i className="bi bi-check-circle-fill"></i>
                          {amenity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card-body">
            <div className="profile-section">
              <h4 className="profile-section-title"><i className="bi bi-exclamation-circle"></i> Complete Your Profile</h4>
              <p className="mb-4">Your profile is not yet set up. Please complete your profile to start using Unissn.</p>
              <button className="edit-profile-btn" onClick={toggleEditMode}>
                <i className="bi bi-plus-circle"></i> Create Profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
